/**
 * DonateForm.js — Stripe Payment Element
 * Renders Card, Google Pay, Apple Pay, Link, and any other methods
 * enabled in your Stripe dashboard — all in one unified UI.
 *
 * Flow:
 *   Picker → Continue → POST /api/donations/create-intent
 *   → Payment Element mounts → user pays
 *   → POST /api/donations/confirm → DB record + receipt email
 */

import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { FaShieldHalved, FaChartBar, FaFileLines, FaHeartPulse, FaLock } from "react-icons/fa6";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import "./DonateForm.css";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (d = 0) => ({
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease, delay: d } },
});

const AMOUNTS = [25, 50, 100, 250, 500];
const CAMPAIGNS = [
  { value: "General Fund", label: "General Fund" },
  { value: "Retreat Support", label: "Retreat Support" },
  { value: "Sponsor a Veteran", label: "Sponsor a Veteran" },
  { value: "Building Fund", label: "Building Fund" },
];
const TRUST_ITEMS = [
  { Icon: FaShieldHalved, label: "Secure Donations", desc: "256-bit SSL encryption on every transaction." },
  { Icon: FaChartBar, label: "Transparent Impact", desc: "We're committed to full accountability." },
  { Icon: FaFileLines, label: "Tax Deductible", desc: "All donations are tax-deductible in the U.S." },
  { Icon: FaHeartPulse, label: "100% Mission Focused", desc: "Every dollar goes directly to the mission." },
];

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const apiFetch = (path, opts = {}) => fetch(`${API}${path}`, { headers: { "Content-Type": "application/json" }, ...opts }).then((r) => r.json());

/* ── Inner Stripe form — mounts once we have clientSecret ── */
function StripeCheckoutForm({ amount, currency, campaign, name, email, onSuccess, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError("");

    const { error: stripeErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/?donation=success`,
        payment_method_data: {
          billing_details: { name: name || "Anonymous", email: email || undefined },
        },
      },
      redirect: "if_required",
    });

    if (stripeErr) {
      setError(stripeErr.message || "Payment failed.");
      setBusy(false);
      return;
    }

    // Save to DB + trigger receipt email (non-blocking)
    apiFetch("/donations/confirm", {
      method: "POST",
      body: JSON.stringify({ payment_intent_id: paymentIntent.id, name, email, amount, currency, campaign }),
    }).catch(() => {});

    onSuccess();
  };

  return (
    <form onSubmit={submit} className="dnform__stripe-form">
      <div className="dnform__payment-element-wrap">
        <PaymentElement options={{ layout: { type: "tabs", defaultCollapsed: false } }} />
      </div>
      {error && (
        <div className="dnform__stripe-error">
          <span>⚠</span> {error}
        </div>
      )}
      <button type="submit" className="dnform__donate-btn" disabled={!stripe || busy} style={{ marginTop: "1.25rem" }}>
        {busy ? (
          <>
            <span className="dnform__spinner" /> Processing…
          </>
        ) : (
          <>
            <FaLock style={{ fontSize: "0.7rem", opacity: 0.7 }} /> Donate ${parseFloat(amount).toFixed(2)} {currency.toUpperCase()}
          </>
        )}
      </button>
      <p className="dnform__secure" style={{ marginTop: "0.65rem" }}>
        <FaLock aria-hidden="true" /> Secured by Stripe · your card data never touches our servers
      </p>
      <button type="button" className="dnform__back-link" onClick={onBack} disabled={busy}>
        ← Change amount or details
      </button>
    </form>
  );
}

export default function DonateForm() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  // Stripe / status
  const [stripePromise, setStripePromise] = useState(null);
  const [donationsEnabled, setDonationsEnabled] = useState(true);
  const [stripeReady, setStripeReady] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  // Step: "picker" | "checkout" | "success"
  const [step, setStep] = useState("picker");

  // Picker
  const [frequency, setFrequency] = useState("one-time");
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [campaign, setCampaign] = useState("General Fund");
  const [donorName, setDonorName] = useState("");
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [dedicate, setDedicate] = useState(false);

  // Checkout
  const [clientSecret, setClientSecret] = useState("");
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentErr, setIntentErr] = useState("");

  const eff = custom ? parseFloat(custom) || 0 : amount;
  const CUR = "usd";

  // Load status + stripe key
  useEffect(() => {
    apiFetch("/donations/status")
      .then((res) => {
        const d = res.data || res;
        setDonationsEnabled(d.enabled !== false);
        setStripeReady(!!d.stripeConfigured);
        if (d.stripeConfigured && d.publishableKey) {
          setStripePromise(loadStripe(d.publishableKey));
        }
      })
      .catch(() => {})
      .finally(() => setStatusLoading(false));
  }, []);

  const goCheckout = async () => {
    setEmailErr("");
    setIntentErr("");
    if (!email.trim()) {
      setEmailErr("Email is required for your receipt.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailErr("Please enter a valid email.");
      return;
    }
    if (!eff || eff < 1) {
      setEmailErr("Please select or enter a donation amount.");
      return;
    }

    if (!stripeReady) {
      // Stripe not configured — save as pending via legacy endpoint
      apiFetch("/donations", { method: "POST", body: JSON.stringify({ amount: eff, currency: "USD", campaign, name: donorName || null, email }) })
        .then(() => setStep("success"))
        .catch((err) => setIntentErr(err.message || "Submission failed."));
      return;
    }

    setIntentLoading(true);
    try {
      const res = await apiFetch("/donations/create-intent", {
        method: "POST",
        body: JSON.stringify({ amount: eff, currency: CUR, campaign, name: donorName || null, email }),
      });
      const d = res.data || res;
      if (!d.clientSecret) throw new Error(res.message || "Could not initialise payment.");
      setClientSecret(d.clientSecret);
      setStep("checkout");
    } catch (err) {
      setIntentErr(err.message || "Could not start payment. Please try again.");
    } finally {
      setIntentLoading(false);
    }
  };

  return (
    <section className="dnform" ref={ref} id="donate-form">
      <div className="dnform__inner container-fg container-2xxl">
        {/* ── Left ── */}
        <motion.div className="dnform__left" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <p className="dnform__eyebrow u-eyebrow">Make a Difference</p>
          <h2 className="dnform__headline u-display">
            Your Gift
            <br />
            Changes Lives.
          </h2>
          <span className="dnform__rule" aria-hidden="true" />
          <p className="dnform__sub">Every donation directly supports our mission to provide world-class retreats at no cost to those who need them most.</p>
          <div className="dnform__badge">
            <div className="dnform__badge-icon" aria-hidden="true">
              <svg viewBox="0 0 44 44" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 36S6 26 6 15a8 8 0 0116-3.2A8 8 0 0138 15c0 11-16 21-16 21z" />
                <path d="M8 30c0 4 3 6 7 7" />
                <path d="M36 30c0 4-3 6-7 7" />
              </svg>
            </div>
            <p className="dnform__badge-text">
              Forge is a 501(c)(3) non-profit organization.
              <br />
              All donations are tax-deductible.
            </p>
          </div>

          {/* Accepted payment logos */}
          {!statusLoading && stripeReady && (
            <div className="dnform__accepted">
              <p className="dnform__accepted-label">Accepted payment methods</p>
              <div className="dnform__accepted-logos">
                {/* Visa */}
                <div className="dnform__pm-chip dnform__pm-chip--visa">
                  <svg viewBox="0 0 38 12" aria-label="Visa">
                    <text x="0" y="11" fontFamily="Arial,sans-serif" fontSize="14" fontWeight="900" fill="#1A1F71">
                      VISA
                    </text>
                  </svg>
                </div>
                {/* Mastercard */}
                <div className="dnform__pm-chip">
                  <svg viewBox="0 0 36 22" aria-label="Mastercard">
                    <circle cx="13" cy="11" r="9" fill="#EB001B" />
                    <circle cx="23" cy="11" r="9" fill="#F79E1B" />
                    <path d="M18 4.5a9 9 0 010 13A9 9 0 0118 4.5z" fill="#FF5F00" />
                  </svg>
                </div>
                {/* Amex */}
                <div className="dnform__pm-chip dnform__pm-chip--amex">
                  <svg viewBox="0 0 44 14" aria-label="American Express">
                    <text x="1" y="11" fontFamily="Arial,sans-serif" fontSize="10" fontWeight="800" fill="white" letterSpacing="1">
                      AMEX
                    </text>
                  </svg>
                </div>
                {/* Google Pay */}
                <div className="dnform__pm-chip dnform__pm-chip--gpay">
                  <svg viewBox="0 0 52 18" aria-label="Google Pay">
                    <text x="0" y="14" fontFamily="'Product Sans',Arial,sans-serif" fontSize="13" fontWeight="700">
                      <tspan fill="#4285F4">G</tspan>
                      <tspan fill="#EA4335">o</tspan>
                      <tspan fill="#FBBC04">o</tspan>
                      <tspan fill="#4285F4">g</tspan>
                      <tspan fill="#34A853">l</tspan>
                      <tspan fill="#EA4335">e </tspan>
                      <tspan fill="#5F6368">Pay</tspan>
                    </text>
                  </svg>
                </div>
                {/* Apple Pay */}
                <div className="dnform__pm-chip dnform__pm-chip--apay">
                  <svg viewBox="0 0 56 18" aria-label="Apple Pay">
                    <text x="1" y="13" fontFamily="-apple-system,'SF Pro Display',sans-serif" fontSize="12" fontWeight="600" fill="white">
                      Apple Pay
                    </text>
                  </svg>
                </div>
                {/* Link */}
                <div className="dnform__pm-chip dnform__pm-chip--link">
                  <svg viewBox="0 0 40 18" aria-label="Link by Stripe">
                    <text x="6" y="13" fontFamily="Arial,sans-serif" fontSize="10" fontWeight="800" fill="white">
                      Link
                    </text>
                  </svg>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* ── Right — widget ── */}
        <motion.div className="dnform__widget" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
          {statusLoading ? (
            <div className="dnform__loading">
              <div className="dnform__spinner dnform__spinner--lg" />
            </div>
          ) : !donationsEnabled ? (
            <div className="dnform__disabled">
              <div className="dnform__disabled-icon">🙏</div>
              <h3 className="dnform__disabled-title">Donations Temporarily Paused</h3>
              <p className="dnform__disabled-text">
                We're not currently accepting online donations, but we deeply appreciate your generosity. Please check back soon or <a href="/contact">reach out to us</a>.
              </p>
            </div>
          ) : step === "success" ? (
            <motion.div className="dnform__success-v2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="dnform__success-icon-lg">🎉</div>
              <h3 className="dnform__success-title">Thank You!</h3>
              <p className="dnform__success-amt">${parseFloat(eff).toFixed(2)} USD</p>
              <p className="dnform__success-text">
                Your donation has been received. A receipt has been sent to <strong>{email}</strong>.
              </p>
              <p className="dnform__success-sub">Your generosity directly helps veterans, first responders, and Gold Star families heal and thrive.</p>
            </motion.div>
          ) : step === "checkout" && clientSecret && stripePromise ? (
            <div className="dnform__checkout">
              {/* Summary bar */}
              <div className="dnform__checkout-bar">
                <div>
                  <p className="dnform__checkout-label">Donating</p>
                  <p className="dnform__checkout-amount">
                    ${parseFloat(eff).toFixed(2)} <span>USD</span>
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p className="dnform__checkout-label">Campaign</p>
                  <p className="dnform__checkout-campaign">{campaign}</p>
                </div>
              </div>
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: "flat",
                    variables: {
                      colorPrimary: "#b85c38",
                      colorBackground: "#ffffff",
                      colorText: "#1b1b1b",
                      colorDanger: "#dc2626",
                      fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
                      borderRadius: "7px",
                    },
                    rules: {
                      ".Input": { border: "1px solid #ddd5c8", boxShadow: "none" },
                      ".Input:focus": { border: "1px solid #b85c38", boxShadow: "0 0 0 3px rgba(184,92,56,0.12)" },
                      ".Tab": { border: "1px solid #ddd5c8" },
                      ".Tab--selected": { border: "1px solid #b85c38", boxShadow: "0 0 0 2px rgba(184,92,56,0.2)" },
                      ".Label": { fontWeight: "600" },
                    },
                  },
                }}
              >
                <StripeCheckoutForm
                  amount={eff}
                  currency={CUR}
                  campaign={campaign}
                  name={donorName}
                  email={email}
                  onSuccess={() => setStep("success")}
                  onBack={() => {
                    setStep("picker");
                    setClientSecret("");
                  }}
                />
              </Elements>
            </div>
          ) : (
            /* ── Picker ── */
            <>
              {/* Frequency */}
              <div className="dnform__freq" role="group" aria-label="Gift frequency">
                {["one-time", "monthly"].map((f) => (
                  <button key={f} type="button" className={`dnform__freq-btn ${frequency === f ? "dnform__freq-btn--active" : ""}`} onClick={() => setFrequency(f)}>
                    {f === "one-time" ? "One-Time Gift" : "Monthly Gift"}
                  </button>
                ))}
              </div>

              {/* Amounts */}
              <div className="dnform__amounts" role="group" aria-label="Select donation amount">
                {AMOUNTS.map((val) => (
                  <button
                    key={val}
                    type="button"
                    className={`dnform__amt-btn ${amount === val && !custom ? "dnform__amt-btn--active" : ""}`}
                    onClick={() => {
                      setAmount(val);
                      setCustom("");
                    }}
                  >
                    ${val}
                  </button>
                ))}
                <input
                  type="number"
                  min="1"
                  placeholder="OTHER"
                  value={custom}
                  onChange={(e) => {
                    setCustom(e.target.value);
                    setAmount(null);
                  }}
                  className={`dnform__amt-btn dnform__amt-input ${custom ? "dnform__amt-btn--active" : ""}`}
                  aria-label="Custom amount"
                />
              </div>

              {/* Campaign */}
              <div className="dnform__field">
                <label className="dnform__field-label">Campaign</label>
                <div className="dnform__select-wrap">
                  <select className="dnform__select" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
                    {CAMPAIGNS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <span className="dnform__select-chevron" aria-hidden="true">
                    ▾
                  </span>
                </div>
              </div>

              {/* Name */}
              <div className="dnform__field">
                <label className="dnform__field-label">
                  Your Name <span className="dnform__field-opt">(optional)</span>
                </label>
                <input type="text" className="dnform__input" placeholder="Full name" value={donorName} onChange={(e) => setDonorName(e.target.value)} />
              </div>

              {/* Email */}
              <div className="dnform__field">
                <label className="dnform__field-label">
                  Email Address <span className="dnform__field-req">*</span>
                </label>
                <input
                  type="email"
                  className={`dnform__input ${emailErr ? "dnform__input--error" : ""}`}
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailErr("");
                  }}
                />
                {emailErr ? <p className="dnform__field-error">{emailErr}</p> : <p className="dnform__field-hint">Your receipt will be emailed here.</p>}
              </div>

              {/* Dedicate */}
              <label className="dnform__dedicate">
                <input type="checkbox" checked={dedicate} onChange={(e) => setDedicate(e.target.checked)} className="dnform__checkbox" />
                <span className="dnform__dedicate-label">Dedicate this gift in honor or memory of someone</span>
              </label>

              {intentErr && (
                <div className="dnform__stripe-error" style={{ marginBottom: "0.5rem" }}>
                  <span>⚠</span> {intentErr}
                </div>
              )}

              <button type="button" className="dnform__donate-btn" onClick={goCheckout} disabled={!eff || intentLoading}>
                {intentLoading ? (
                  <>
                    <span className="dnform__spinner" /> Setting up payment…
                  </>
                ) : (
                  `Continue to Payment — $${parseFloat(eff || 0).toFixed(2)}`
                )}
              </button>

              {stripeReady && (
                <div className="dnform__methods-strip">
                  <FaLock style={{ fontSize: "0.62rem", opacity: 0.45 }} />
                  <span>Card · Google Pay · Apple Pay · Link · and more via Stripe</span>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* ── Trust strip ── */}
      <div className="dnform__trust">
        <div className="container-fg container-2xxl">
          <motion.div className="dnform__trust-header" variants={fadeUp(0.05)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <span className="dnform__trust-rule" aria-hidden="true" />
            <p className="dnform__trust-eyebrow u-eyebrow">You Can Trust Us</p>
            <span className="dnform__trust-rule" aria-hidden="true" />
          </motion.div>
          <div className="dnform__trust-grid">
            {TRUST_ITEMS.map((t, i) => (
              <motion.div key={t.label} className="dnform__trust-item" variants={fadeUp(0.08 + i * 0.08)} initial="hidden" animate={inView ? "show" : "hidden"}>
                <div className="dnform__trust-icon" aria-hidden="true">
                  <t.Icon size={35} />
                </div>
                <div className="dnform__trust-text">
                  <p className="dnform__trust-label">{t.label.toUpperCase()}</p>
                  <p className="dnform__trust-desc">{t.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
